import React, { useState } from 'react';
import { Button, FileInput, TextInput, Spinner, Alert, Progress, Textarea } from 'flowbite-react';
import Label from "../../../components/Label"
import { FaUpload, FaTimesCircle, FaCheckCircle } from 'react-icons/fa';
import api from '../../../services/api/axiosConfig'; 
import { useNavigate, useParams } from 'react-router-dom';
import ShowValidationMessage from '../../../components/ShowValidationMessage';
import errorHandler from '../../../utils/errorHandler';
import Modal from '../../../components/Modal';
import API_ENDPOINTS,{buildEndpoint} from '../../../services/api/endpoints';
import { toast } from 'react-toastify';

// Helper to format file size (moved here for convenience)
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const UploadCustomerDocument = ({ isOpen, onClose,onUploadSuccess,customerId }) => {
    // The route is /dashboard/customer-document/:id/upload, so the param is 'id'
    const navigate = useNavigate();

    const [selectedFile, setSelectedFile] = useState(null);
    const [notes, setNotes] = useState('');
    const [document_name, setDocumentName] = useState('');

    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(null);
    const [errors, setErrors] = useState({}); // <--- ADD THIS LINE to define errors state

    const handleFileChange = (e) => {
        setErrors({}); // Clear validation errors on new file selection
        setUploadSuccess(null);
        setUploadError(null);
        setUploadProgress(0);
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
        } else {
            setSelectedFile(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        if (!selectedFile) {
            setUploadError('Please select a file to upload.');
            return;
        }
        if (!customerId) {
            // This error should now rarely happen if route and link are correct
            setUploadError('Customer ID is missing. Cannot upload document.');
            return;
        }

        setUploading(true);
        setUploadError(null);
        setUploadSuccess(null);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('notes', notes);
        formData.append('document_name', document_name);


        try {
            const response = await api.post(buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.DOCUMENT.CREATE,{customerId}), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                },
            });
            toast.success("Document uploaded successfully!");
            setSelectedFile(null);
            setNotes('');
            setUploadProgress(100);
            // Navigate back to the customer's detail page (adjust path if needed)
            if (onUploadSuccess) {
                onUploadSuccess();
            }
        } catch (err) {
            console.error('Upload failed:', err);
            setUploadSuccess(null);
           
            errorHandler(err, setErrors);

        } finally {
            setUploading(false);
        }
    };

    if (!customerId) {
        // This is a fallback render if customerId is truly missing (e.g., direct bad URL entry)
        return (
            <div className="container mx-auto p-6">
                <Alert color="failure">
                    <span className="font-medium">Error!</span> Customer ID is missing from the URL.
                    Please navigate from a customer's detail page.
                </Alert>
                <Button className="mt-4" onClick={() => navigate('/dashboard/customer-list')}>Go to Customer List</Button>
            </div>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Update Customer document">

        
                {uploadError && (
                    <Alert color="failure" onDismiss={() => setUploadError(null)} className="mb-3">
                        <FaTimesCircle className="mr-2" /> {uploadError}
                    </Alert>
                )}
                {uploadSuccess && (
                    <Alert color="success" onDismiss={() => setUploadSuccess(null)} className="mb-3">
                        <FaCheckCircle className="mr-2" /> {uploadSuccess}
                    </Alert>
                )}
                <form onSubmit={handleSubmit}>

                       <div className="mb-4">
                        <div className="mb-2 block">
                            <Label htmlFor="document_name" label="Document name" />
                        </div>
                        <TextInput
                            id="document_name"
                            name="document_name"
                            placeholder="Write document name"
                            value={document_name}
                            onChange={(e) => setDocumentName(e.target.value)}
                        />
                    </div>

                    <div className="mb-4">
                        <div className="mb-2 block">
                            <Label htmlFor="file" label="Select File" />
                        </div>
                        <FileInput id="file" onChange={handleFileChange} required />
                        {selectedFile && (
                            <p className="text-sm text-gray-500 mt-1">Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})</p>
                        )}
                    <ShowValidationMessage error={errors.file} />


                    </div>

                    <div className="mb-4">
                        <div className="mb-2 block">
                            <Label htmlFor="notes" label="Notes (Optional)" />
                        </div>
                        <Textarea
                            id="notes"
                            placeholder="Add any relevant notes here"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>

                    {uploading && (
                        <div className="w-full mb-4">
                            <Progress progress={uploadProgress} textLabel="Uploading" size="lg" color="blue" />
                            <span className="text-sm text-gray-500 mt-1 block">{(uploadProgress === 100 && !uploadSuccess) ? 'Processing...' : ''}</span>
                        </div>
                    )}

                    <Button type="submit" disabled={uploading}>
                        {uploading ? <Spinner size="sm" className="mr-3" /> : <FaUpload className="mr-3" />}
                        {uploading ? 'Uploading...' : 'Upload Document'}
                    </Button>
                </form>
        </Modal>
    );
};

export default UploadCustomerDocument;